import React, { PureComponent } from 'react';
import { MapContext } from 'react-openlayers';
import { layer, source, feature } from 'react-openlayers';
import { convertProjection4329, generateColor } from '../../../utils';
import { Equipment } from '@rogoag/airtable/equipment';

interface LiveTruckLayerProps {
  allTrucks: Equipment[];
}

interface LiveTruckLayerState {
  truckFeatures: {
    [key: string]: {
      coords: number[];
    };
  };
  hoveredTruck: string | null;
}

export default class LiveTruckLayer extends PureComponent<LiveTruckLayerProps, LiveTruckLayerState> {
  static contextType = MapContext;
  declare context: React.ContextType<typeof MapContext>;

  // See MapInteractionsLayer.waitForMap for the full rationale. Any
  // unmount with a pending retry leaks the callback onto the old
  // instance, which then attaches a SECOND pointermove listener to
  // the live map — hover handling then fires twice per move.
  waitForMapTimer: NodeJS.Timeout | undefined;

  constructor(props: LiveTruckLayerProps) {
    super(props);

    this.state = {
      truckFeatures: {},
      hoveredTruck: null,
    };
  }

  componentDidMount() {
    this.loadAllTrucks();
    this.waitForMap();
  }

  waitForMap = () => {
    if (this.context?.map) {
      this.context.map.on('pointermove', this.handlePointerMove);
    } else {
      this.waitForMapTimer = setTimeout(this.waitForMap, 100); // try again in 100ms
    }
  };

  componentDidUpdate(prevProps: LiveTruckLayerProps) {
    if (this.props.allTrucks !== prevProps.allTrucks) {
      this.loadAllTrucks();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.waitForMapTimer);
    this.waitForMapTimer = undefined;
    this.detachPointerMoveListener();
  }

  detachPointerMoveListener() {
    const map = this.context?.map;

    if (map) {
      map.un('pointermove', this.handlePointerMove);
    }
  }

  handlePointerMove = (event: any) => {
    const map = this.context?.map;

    if (!map) {
      return;
    }

    const pixel = map.getEventPixel(event.originalEvent);
    const featureAtPixel = map.forEachFeatureAtPixel(pixel, (feat) => feat);

    if (featureAtPixel) {
      const name = featureAtPixel.get('name');

      if (name && this.state.truckFeatures[name]) {
        this.setState({ hoveredTruck: name });

        return;
      }
    }

    if (this.state.hoveredTruck !== null) {
      this.setState({ hoveredTruck: null });
    }
  };

  async loadAllTrucks() {
    const truckFeatures: Record<string, { coords: number[] }> = {};

    for (const truckRecord of this.props.allTrucks || []) {
      const coordinates = truckRecord.latestCoordinateDashMomentum;

      if (!coordinates) {
        continue;
      }

      const [lat, lon] = coordinates.split(',').map(parseFloat);

      if (!lat || !lon) {
        continue;
      }

      const coords = convertProjection4329([lon, lat]);
      const truckName = truckRecord.equipment;

      if (!truckName) {
        continue;
      }

      truckFeatures[truckName] = { coords };
    }

    this.setState({ truckFeatures });
  }

  render() {
    console.debug(`LiveTruckLayer: ${Object.keys(this.state.truckFeatures).length} trucks`);
    const { truckFeatures, hoveredTruck } = this.state;

    return (
      // @ts-ignore
      <layer.Vector zIndex={2}>
        {/* @ts-ignore */}
        <source.VectorSourceReact>
          {Object.entries(truckFeatures).map(([truck, { coords }]) => (
            <feature.PointReact
              key={truck}
              coordinate={coords}
              properties={{ name: truck }}
              iconOptions={{
                src: './images/truck.png',
                scale: 0.07,
                color: generateColor(truck),
              }}
            />
          ))}

          {/* Hover label */}
          {hoveredTruck && truckFeatures[hoveredTruck] && (
            <feature.PointReact
              key={`label-${hoveredTruck}`}
              coordinate={truckFeatures[hoveredTruck].coords}
              properties={{ name: hoveredTruck }} // optional
              textOptions={{
                text: hoveredTruck,
                offsetY: -20,
                font: '14px Calibri,sans-serif',
              }}
            />
          )}
        </source.VectorSourceReact>
      </layer.Vector>
    );
  }
}
